/**
 * Alpine.js Language Overflow Component
 * Shows only languages that fit on one line with a "+x" indicator for overflow
 */

import { scheduleLanguageOverflowMeasurement } from './language-overflow-measurement.js';

export function languageOverflow(totalCount) {
    return {
        totalCount,
        visibleCount: totalCount,
        hiddenCount: 0,
        initialized: false,
        _destroyed: false,

        init() {
            // Run calculation after DOM is ready
            this.$nextTick(() => {
                if (this._destroyed) return;
                this.calculateVisible();
            });

            // Recalculate on window resize
            this._resizeHandler = this.debounce(() => {
                this.calculateVisible();
            }, 150);
            window.addEventListener('resize', this._resizeHandler);
        },

        destroy() {
            this._destroyed = true;
            this._cancelMeasurement?.();
            this._cancelMeasurement = null;
            this._resizeHandler?.cancel();
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
            }
        },

        calculateVisible() {
            if (this._destroyed) return;
            const container = this.$el;
            if (!container) return;

            const chips = container.querySelectorAll('.lang-chip');
            const overflowBadgeWidth = 45; // Approximate width of "+x" badge
            const gap = 6; // gap-1.5 = 6px
            this._cancelMeasurement?.();
            this._cancelMeasurement = scheduleLanguageOverflowMeasurement(container, chips, ({ containerWidth, chipWidths }) => {
                if (this._destroyed) return;
                const totalWidth = chipWidths.reduce((width, chipWidth, index) => width + chipWidth + (index > 0 ? gap : 0), 0);
                let visible = chipWidths.length;

                if (totalWidth > containerWidth) {
                    let usedWidth = 0;
                    visible = 0;
                    chipWidths.forEach((chipWidth) => {
                        const spaceNeeded = usedWidth + chipWidth + (visible > 0 ? gap : 0) + overflowBadgeWidth + gap;
                        if (spaceNeeded <= containerWidth) {
                            usedWidth += chipWidth + (visible > 0 ? gap : 0);
                            visible++;
                        }
                    });
                    visible = Math.max(1, visible);
                }

                this.visibleCount = visible;
                this.hiddenCount = Math.max(0, this.totalCount - visible);
                this.initialized = true;
                this._cancelMeasurement = null;
            });
        },

        isVisible(index) {
            // Show all until initialized, then only show visible count
            return !this.initialized || index < this.visibleCount;
        },

        debounce(fn, delay) {
            let timeoutId;
            const debounced = (...args) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => fn.apply(this, args), delay);
            };
            debounced.cancel = () => {
                clearTimeout(timeoutId);
                timeoutId = null;
            };
            return debounced;
        }
    };
}
